import React, { useEffect, useMemo, useRef, useState } from 'react'
import classnames from 'classnames'

import { Button, BUTTON_THEME, Icon } from '@st-operations/uikit/src/v2/index.js'

import RadioGroup from '_components/radio-input-group'

import AudienceLink from '../components/audience-link'
import ProgressBar from '../components/progress-bar'
import { changesToBody } from '../lib/operations'
import { downloadSessionReport } from '../lib/report'
import { RESULT_STATUS, saveSession } from '../lib/session-store'

// if this many audiences in a row fail with the same error, the problem is
// systemic (e.g. expired session) — stop instead of failing the whole batch
const MAX_CONSECUTIVE_IDENTICAL_ERRORS = 3

const STATUS_LABELS = {
  [RESULT_STATUS.PENDING]: 'Pending',
  [RESULT_STATUS.RUNNING]: 'Updating…',
  [RESULT_STATUS.SUCCESS]: 'Updated',
  [RESULT_STATUS.ERROR]: 'Error',
  [RESULT_STATUS.PERSISTENT_ERROR]: 'Persistent error',
  [RESULT_STATUS.SKIPPED]: 'Skipped',
}

const StatusBadge = ({ status }) => (
  <span className={classnames('bulk-edit-run__status', `bulk-edit-run__status--${status}`)}>
    <Choose>
      <When condition={status === RESULT_STATUS.SUCCESS}>
        <Icon icon="checkmark" type="fill" width="14px" height="14px" color="#13682B" />
      </When>
      <When condition={status === RESULT_STATUS.ERROR || status === RESULT_STATUS.PERSISTENT_ERROR}>
        <Icon icon="error" type="fill" width="14px" height="14px" color="#C0392B" />
      </When>
      <When condition={status === RESULT_STATUS.RUNNING}>
        <span className="bulk-edit-run__spinner" />
      </When>
      <Otherwise>
        <span className="bulk-edit-run__dot" />
      </Otherwise>
    </Choose>
    {STATUS_LABELS[status] || status}
  </span>
)

const RunStage = ({ collection, session, onSessionChange, items, onNewBulkEdit }) => {
  const [results, setResults] = useState(session.results)
  const [running, setRunning] = useState(false)
  const [showOnlyFailures, setShowOnlyFailures] = useState(false)

  // refs keep the loop in sync without waiting for re-renders
  const resultsRef = useRef(session.results)
  const sessionRef = useRef(session)
  const stopRef = useRef(false)

  const setResult = (id, patch) => {
    resultsRef.current = {
      ...resultsRef.current,
      [id]: { ...resultsRef.current[id], ...patch },
    }
    setResults(resultsRef.current)
  }

  const persist = (status) => {
    sessionRef.current = saveSession({
      ...sessionRef.current,
      status,
      results: resultsRef.current,
    })
    onSessionChange(sessionRef.current)
  }

  const run = async (itemsToRun) => {
    setRunning(true)
    stopRef.current = false
    persist('running')

    let lastErrorMessage = null
    let consecutiveErrors = 0
    let abortedByErrors = false

    for (const { audience, changes } of itemsToRun) {
      if (stopRef.current || abortedByErrors) {
        setResult(audience._id, { status: RESULT_STATUS.SKIPPED, changes })
        continue
      }

      setResult(audience._id, { status: RESULT_STATUS.RUNNING, changes })

      try {
        const response = await collection.update(audience._id, changesToBody(changes))
        // some collections (e.g. taxonomy audiences) resolve with a wrapped
        // envelope that can carry a logical failure despite a 200 HTTP status —
        // treat that as an error so it counts as a failure, not an update
        const updateError = collection.getUpdateError?.(response)
        if (updateError) throw new Error(updateError)
        const previous = resultsRef.current[audience._id]
        setResult(audience._id, {
          status: RESULT_STATUS.SUCCESS,
          attempts: (previous.attempts || 0) + 1,
        })
        lastErrorMessage = null
        consecutiveErrors = 0
      } catch (error) {
        const message = error?.message || 'Unknown error'
        const previous = resultsRef.current[audience._id]
        const errors = [...(previous.errors || []), { message, at: new Date().toISOString() }]
        const samePersistentError =
          errors.length >= 2 && errors[errors.length - 2].message === message

        setResult(audience._id, {
          status: samePersistentError ? RESULT_STATUS.PERSISTENT_ERROR : RESULT_STATUS.ERROR,
          attempts: (previous.attempts || 0) + 1,
          errors,
        })

        consecutiveErrors = message === lastErrorMessage ? consecutiveErrors + 1 : 1
        lastErrorMessage = message

        if (consecutiveErrors >= MAX_CONSECUTIVE_IDENTICAL_ERRORS) {
          abortedByErrors = true
          toast.error(
            `Stopped: ${consecutiveErrors} audiences in a row failed with "${message}". Remaining audiences were skipped.`
          )
        }
      }

      // persist after every audience so a crash or refresh keeps the log
      persist('running')
    }

    const hasFailures = Object.values(resultsRef.current).some(({ status }) =>
      [RESULT_STATUS.ERROR, RESULT_STATUS.PERSISTENT_ERROR, RESULT_STATUS.SKIPPED].includes(status)
    )
    persist(
      abortedByErrors || stopRef.current
        ? 'aborted'
        : hasFailures
        ? 'completed_with_errors'
        : 'completed'
    )
    setRunning(false)
    downloadSessionReport(sessionRef.current)
  }

  useEffect(() => {
    run(items)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // discourage closing the tab mid-process
  useEffect(() => {
    if (!running) return
    const warn = (event) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [running])

  const counts = useMemo(
    () =>
      Object.values(results).reduce(
        (acc, { status }) => ({ ...acc, [status]: (acc[status] || 0) + 1 }),
        {}
      ),
    [results]
  )

  const processedCount = items.filter(({ audience }) =>
    [RESULT_STATUS.SUCCESS, RESULT_STATUS.ERROR, RESULT_STATUS.PERSISTENT_ERROR].includes(
      results[audience._id]?.status
    )
  ).length

  // persistent errors are excluded — retrying them would just repeat the same failure
  const retryableItems = items.filter(({ audience }) =>
    [RESULT_STATUS.ERROR, RESULT_STATUS.SKIPPED].includes(results[audience._id]?.status)
  )

  const failureItems = items.filter(({ audience }) =>
    [RESULT_STATUS.ERROR, RESULT_STATUS.PERSISTENT_ERROR, RESULT_STATUS.SKIPPED].includes(
      results[audience._id]?.status
    )
  )

  const visibleItems = showOnlyFailures ? failureItems : items

  const ALL_LABEL = `All (${items.length})`
  const FAILURES_LABEL = `Failures only (${failureItems.length})`

  const filterOptions = [
    { label: ALL_LABEL, checked: !showOnlyFailures },
    { label: FAILURES_LABEL, checked: showOnlyFailures },
  ]

  const handleFilterChange = (event) => setShowOnlyFailures(event.target.value === FAILURES_LABEL)

  return (
    <div className="bulk-edit-run">
      <section className="bulk-edit__card">
        <header className="bulk-edit__card-header">
          <h3>4. Running the bulk update — {collection.label}</h3>
        </header>

        <If condition={running}>
          <div className="bulk-edit__warning">
            Updating one audience at a time from your browser — keep this tab open until it
            finishes.
          </div>
        </If>

        <ProgressBar loaded={processedCount} total={items.length} label="Processed" />

        <p className="bulk-edit__summary">
          {counts[RESULT_STATUS.SUCCESS] || 0} updated ·{' '}
          {(counts[RESULT_STATUS.ERROR] || 0) + (counts[RESULT_STATUS.PERSISTENT_ERROR] || 0)}{' '}
          failed · {counts[RESULT_STATUS.SKIPPED] || 0} skipped
          <If condition={counts[RESULT_STATUS.PERSISTENT_ERROR] > 0}>
            {' '}
            ({counts[RESULT_STATUS.PERSISTENT_ERROR]} with persistent errors, excluded from retries)
          </If>
        </p>

        <div className="bulk-edit-run__filters">
          <span className="bulk-edit-run__filters-label">Show:</span>
          <RadioGroup
            groupName="bulk-edit-run-filter"
            className="bulk-edit-run__filters-radios"
            options={filterOptions}
            onChange={handleFilterChange}
          />
        </div>

        <ul className="bulk-edit-run__list">
          <For each="item" of={visibleItems}>
            <li key={item.audience._id} className="bulk-edit-run__item">
              <StatusBadge status={results[item.audience._id]?.status} />
              <AudienceLink
                className="bulk-edit-run__name"
                href={collection.audienceUrl(item.audience._id)}
                name={item.audience.name}
                id={item.audience._id}
              />
              <If condition={results[item.audience._id]?.errors?.length > 0}>
                <div className="bulk-edit-run__error">
                  {
                    results[item.audience._id].errors[results[item.audience._id].errors.length - 1]
                      .message
                  }{' '}
                  (attempts: {results[item.audience._id].attempts})
                </div>
              </If>
            </li>
          </For>
        </ul>
      </section>

      <div className="bulk-edit__stage-actions">
        <Choose>
          <When condition={running}>
            <Button theme={BUTTON_THEME.SECONDARY} onClick={() => (stopRef.current = true)}>
              Stop (skips the remaining audiences)
            </Button>
          </When>
          <Otherwise>
            <Button theme={BUTTON_THEME.SECONDARY} onClick={onNewBulkEdit}>
              New Bulk Edit
            </Button>
            <If condition={retryableItems.length > 0}>
              <Button theme={BUTTON_THEME.SECONDARY} onClick={() => run(retryableItems)}>
                Retry Failed ({retryableItems.length})
              </Button>
            </If>
            <Button onClick={() => downloadSessionReport(sessionRef.current)}>
              Download Report
            </Button>
          </Otherwise>
        </Choose>
      </div>
    </div>
  )
}

export default RunStage
